import React, { useMemo } from "react";
import { FilterOutlined } from "@ant-design/icons";

import SbTable from "uiCore/SbTable/SbTable";
import useAuthStore from "Store/useAuthStore";
import { buildFieldConfigMap } from "Utils/formatting";

import { PAICTableWrapperStyled } from "./PeoplePlusAIV2.styles";

const PAICRequisitionTableV2 = ({
  loading,
  filteredJobs = [],
  tableFilters = {},
  onFilteredDataChange,
  onTableFiltersChange,
}) => {
  const orgMetaData = useAuthStore((s) => s.orgMetaData);
  const fieldMap = useMemo(() => buildFieldConfigMap(orgMetaData, 'req_no'), [orgMetaData]);
  const getFilters = (key) =>
    [
      ...new Set(
        (filteredJobs || [])
          .map((item) => item?.[key])
          .filter(
            (value) => value !== undefined && value !== null && value !== "",
          ),
      ),
    ]
      .sort((a, b) => {
        const aNum = /^\d/.test(a);
        const bNum = /^\d/.test(b);
        if (aNum && !bNum) return -1;
        if (!aNum && bNum) return 1;
        return a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });
      })
      .map((value) => ({
        text: value,
        value,
    }));

  // Read active filter state from the page so reset and selection stay in sync
  const hasActiveFilters = useMemo(() => {
    return Object.values(tableFilters || {}).some(
      (value) => Array.isArray(value) && value.length > 0,
    );
  }, [tableFilters]);

  const columns = useMemo(
    () => [
      {
        title: fieldMap['job_code']?.label || "Role Id",
        dataIndex: "sb_requisition_number",
        key: "sb_requisition_number",
        render: (text) => text || "-",
      },
      {
        title: "Legal Entity",
        dataIndex: "legal_entity",
        key: "legal_entity",
        render: (text) => text || "-",
      },
      {
        title: "Role Name",
        dataIndex: "job_title",
        key: "job_title",
        render: (text, record) => record?.job_title_for_jd || text || "-",
      },
      {
        title: "Role Type",
        dataIndex: "role_type",
        key: "role_type",
        filters: getFilters("role_type"),
        filteredValue: tableFilters.role_type || null,
        onFilter: (value, record) => record?.role_type === value,
        render: (text) => text || "-",
      },
      {
        title: "Business",
        dataIndex: "business_unit",
        key: "business_unit",
        filters: getFilters("business_unit"),
        filteredValue: tableFilters.business_unit || null,
        onFilter: (value, record) => record?.business_unit === value,
        render: (text) => text || "-",
      },
      {
        title: "Location",
        dataIndex: "location",
        key: "location",
        filters: getFilters("location"),
        filteredValue: tableFilters.location || null,
        onFilter: (value, record) => record?.location === value,
        render: (text) => text || "-",
      },
      {
        title: "Function",
        dataIndex: "function",
        key: "function",
        filters: getFilters("function"),
        filteredValue: tableFilters.function || null,
        onFilter: (value, record) => record?.function === value,
        render: (text) => text || "-",
      },
      {
        title: "Sub-Function",
        dataIndex: "sub_function",
        key: "sub_function",
        filters: getFilters("sub_function"),
        filteredValue: tableFilters.sub_function || null,
        onFilter: (value, record) => record?.sub_function === value,
        render: (text) => text || "-",
      },
      {
        title: "Job Level",
        dataIndex: "job_level",
        key: "job_level",
        filters: getFilters("job_level"),
        filteredValue: tableFilters.job_level || null,
        onFilter: (value, record) => record?.job_level === value,
        render: (text) => text || "-",
      },
      {
        title: "Version",
        dataIndex: "version",
        key: "version",
        render: (text) => text || "-",
      },
    ],
    [filteredJobs, tableFilters, fieldMap],
  );

  const safeData = (filteredJobs || []).filter(
    (job) =>
      job && (job.sb_requisition_number || job.job_title || job.sb_unique_id),
  );

  const handleTableChange = (pagination, filters, sorter, extra) => {
    onTableFiltersChange && onTableFiltersChange(filters || {});
    onFilteredDataChange &&
      onFilteredDataChange(
        extra?.currentDataSource?.length ?? filteredJobs.length,
      );
  };

  return (
    <PAICTableWrapperStyled>
      <SbTable
        columns={columns}
        dataSource={safeData}
        loading={loading}
        rowKey={(record, index) =>
          record?.sb_unique_id ?? record?.job_id ?? index
        }
        sticky
        rowClassName={() => "paic-table-row"}
        pagination={{
          defaultPageSize: 10,
          pageSizeOptions: ["10", "20", "30"],
          showSizeChanger: true,

          // Keep the old footer wording, but keep the styling simple
          showTotal: (total) => (
            <div className="paic-pagination-total">
              <FilterOutlined />
              <span
                className={
                  hasActiveFilters ? "is-active-text" : "is-empty-text"
                }
              >
                {hasActiveFilters
                  ? `${total} Roles selected`
                  : "0 Roles selected. Please select at least one role"}
              </span>
            </div>
          ),
        }}
        onChange={handleTableChange}
      />
    </PAICTableWrapperStyled>
  );
};

export default PAICRequisitionTableV2;